import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { emailMessages } from 'src/features/profile/utils';
import { useListParkingMemberships } from 'src/features/users/api/useListParkingsMemberships';
import CellUserActions from 'src/features/users/components/tables/CellUserActions';
import { statusOptions } from 'src/features/users/utils/constants';
import { userMessages } from 'src/features/users/utils/messages';
import { Membership, MEMBERSHIP_STATUS } from 'src/features/users/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/utils/messages';

const DeactivatedUsersTable = () => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const permissionManageUsers = useHasPermissions([PERMISSION.MANAGE_USERS]);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { data, isLoading, isError } = useListParkingMemberships({
        parkingId,
        user__is_staff: false,
        status: MEMBERSHIP_STATUS.INACTIVE,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        config: { enabled: !!parkingId },
    });

    const columnHelper = createColumnHelper<Membership>();

    const columns = [
        columnHelper.accessor('user.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.name),
            },
        }),
        columnHelper.accessor('user.email', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('sp_role.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.role),
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(userMessages.active),
                badgeOptions: statusOptions,
            },
        }),
        ...(permissionManageUsers
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellUserActions,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Membership, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(userMessages.deactivated),
                entryName: formatMessage(userMessages.user).toLowerCase(),
                entryNames: formatMessage(userMessages.users).toLowerCase(),
                placeholder: `${formatMessage(generalMessages.name)}, ${formatMessage(emailMessages.email)}`,
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default DeactivatedUsersTable;
